import React, { useState, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import emailjs from '@emailjs/browser';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideIn = keyframes`
  from {
    transform: translateX(-100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

const confetti = keyframes`
  0% {
    transform: translateY(0) rotate(0deg);
    opacity: 1;
  }
  100% {
    transform: translateY(100vh) rotate(360deg);
    opacity: 0;
  }
`;

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
`;

const AppointmentForm = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    contactNumber: '',
    service: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const formRef = useRef(null);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.contactNumber.trim()) {
      newErrors.contactNumber = 'Contact number is required';
    }
    
    if (!formData.service) {
      newErrors.service = 'Please select a service';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // Add shake animation to form on error
      if (formRef.current) {
        formRef.current.style.animation = `${shake} 0.5s ease-in-out`;
        setTimeout(() => {
          if (formRef.current) formRef.current.style.animation = '';
        }, 500);
      }
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Simulate API call/email sending
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Uncomment and configure emailjs if you want actual email functionality
      /*
      await emailjs.sendForm(
        'YOUR_SERVICE_ID',
        'YOUR_TEMPLATE_ID',
        e.target,
        'YOUR_USER_ID'
      );
      */
      
      setShowSuccess(true);
      setFormData({
        fullName: '',
        email: '',
        contactNumber: '',
        service: '',
        message: ''
      });
      
      setTimeout(() => {
        setShowSuccess(false);
      }, 5000);
      
    } catch (error) {
      console.error('Error submitting form:', error);
      setErrors({ submit: 'Failed to submit form. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const Confetti = () => (
    <ConfettiContainer>
      {[...Array(50)].map((_, i) => (
        <ConfettiPiece 
          key={i}
          style={{
            left: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 2}s`,
            backgroundColor: `hsl(${Math.random() * 360}, 100%, 50%)`,
            width: `${Math.random() * 10 + 5}px`,
            height: `${Math.random() * 10 + 5}px`,
          }}
        />
      ))}
    </ConfettiContainer>
  );

  if (showSuccess) {
    return (
      <SuccessContainer>
        <Confetti />
        <SuccessCard>
          <SuccessIcon>
            <svg viewBox="0 0 24 24" width="64" height="64">
              <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </SuccessIcon>
          <SuccessTitle>Thank You!</SuccessTitle>
          <SuccessMessage>
            Thank you for your response. We will get back to you soon!
          </SuccessMessage>
          <SuccessSubmessage>
            Our team will contact you within 24 hours to discuss your project.
          </SuccessSubmessage>
          <BackButton onClick={() => setShowSuccess(false)}>
            Submit Another Response
          </BackButton>
        </SuccessCard>
      </SuccessContainer>
    );
  }

  return (
    <FormBackground>
      <FormContainer>
        <AnimatedFormHeading>
          Book an Appointment with Our Top-Rated Digital Marketing Experts
        </AnimatedFormHeading>
        <AnimatedFormSubtitle>
          You've seen the great things SRK Softwares can do. Now, it's time to discuss your project
          with a real person. Don't wait any longer!
        </AnimatedFormSubtitle>
        
        <Form ref={formRef} onSubmit={handleSubmit}>
          <FormGroup>
            <Input
              type="text"
              id="fullName"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              placeholder="👤 Enter your full name"
              required
              hasError={!!errors.fullName}
            />
            {errors.fullName && <ErrorText>{errors.fullName}</ErrorText>}
          </FormGroup>
          
          <FormGroup>
            <Input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="✉️ Enter your email address"
              required
              hasError={!!errors.email}
            />
            {errors.email && <ErrorText>{errors.email}</ErrorText>}
          </FormGroup>
          
          <FormGroup>
            <Input
              type="tel"
              id="contactNumber"
              name="contactNumber"
              value={formData.contactNumber}
              onChange={handleChange}
              placeholder="📞 Enter your contact number"
              required
              hasError={!!errors.contactNumber}
            />
            {errors.contactNumber && <ErrorText>{errors.contactNumber}</ErrorText>}
          </FormGroup>
          
          <FormGroup>
            <Select
              id="service"
              name="service"
              value={formData.service}
              onChange={handleChange}
              required
              hasError={!!errors.service}
            >
              <option value="">🎯 Select a service you're interested in</option>
              <option value="SEO">🔍 SEO Optimization</option>
              <option value="PPC">💰 PPC Advertising</option>
              <option value="Social Media">📱 Social Media Marketing</option>
              <option value="Content">📝 Content Marketing</option>
              <option value="Email">📧 Email Marketing</option>
              <option value="Other">💡 Other Services</option>
            </Select>
            {errors.service && <ErrorText>{errors.service}</ErrorText>}
          </FormGroup>
          
          <FormGroup>
            <TextArea
              id="message"
              name="message"
              rows="4"
              value={formData.message}
              onChange={handleChange}
              placeholder="💬 Tell us about your project..."
              required
              hasError={!!errors.message}
            />
            {errors.message && <ErrorText>{errors.message}</ErrorText>}
          </FormGroup>
          
          {errors.submit && <SubmitError>{errors.submit}</SubmitError>}
          
          <SubmitButton 
            type="submit" 
            disabled={isSubmitting}
            isSubmitting={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Spinner />
                Sending...
              </>
            ) : (
              '🚀 Register Today'
            )}
          </SubmitButton>
        </Form>
      </FormContainer>
    </FormBackground>
  );
};

// Styled components
const FormBackground = styled.div`
  background: linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), 
              url('https://images.unsplash.com/photo-1556761175-b413da4baf72?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80') center/cover;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const FormContainer = styled.div`
  max-width: 600px;
  width: 100%;
  margin: 2rem auto;
  padding: 3rem;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  animation: ${fadeIn} 0.8s ease-out;
  
  @media (max-width: 768px) {
    padding: 2rem;
    margin: 1rem;
  }
`;

const AnimatedFormHeading = styled.h2`
  color: #2d3748;
  text-align: center;
  margin-bottom: 1rem;
  font-size: 1.8rem;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: ${slideIn} 0.8s ease-out;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const AnimatedFormSubtitle = styled.p`
  color: #718096;
  text-align: center;
  margin-bottom: 2rem;
  line-height: 1.6;
  font-size: 1.1rem;
  animation: ${fadeIn} 0.8s ease-out 0.2s both;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  animation: ${fadeIn} 0.8s ease-out 0.4s both;
`;

const FormGroup = styled.div`
  margin-bottom: 1.8rem;
  position: relative;
`;

const ErrorText = styled.span`
  color: #e53e3e;
  font-size: 0.8rem;
  font-weight: 500;
  margin-top: 0.5rem;
  display: block;
`;

const Input = styled.input`
  width: 100%;
  padding: 16px 20px;
  border: 2px solid ${props => props.hasError ? '#e53e3e' : '#e2e8f0'};
  border-radius: 12px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.9);
  font-family: inherit;
  
  &::placeholder {
    color: #a0aec0;
    font-weight: 500;
  }
  
  &:focus {
    border-color: ${props => props.hasError ? '#e53e3e' : '#667eea'};
    outline: none;
    box-shadow: 0 0 0 3px ${props => props.hasError ? 'rgba(229, 62, 62, 0.1)' : 'rgba(102, 126, 234, 0.1)'};
    transform: translateY(-2px);
    background: white;
  }
  
  &:hover {
    border-color: ${props => props.hasError ? '#e53e3e' : '#cbd5e0'};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 16px 20px;
  border: 2px solid ${props => props.hasError ? '#e53e3e' : '#e2e8f0'};
  border-radius: 12px;
  font-size: 1rem;
  background-color: rgba(255, 255, 255, 0.9);
  transition: all 0.3s ease;
  font-family: inherit;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=US-ASCII,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 5'><path fill='%23666' d='M2 0L0 2h4zm0 5L0 3h4z'/></svg>");
  background-repeat: no-repeat;
  background-position: right 15px center;
  background-size: 12px;
  
  &::placeholder {
    color: #a0aec0;
  }
  
  &:focus {
    border-color: ${props => props.hasError ? '#e53e3e' : '#667eea'};
    outline: none;
    box-shadow: 0 0 0 3px ${props => props.hasError ? 'rgba(229, 62, 62, 0.1)' : 'rgba(102, 126, 234, 0.1)'};
    transform: translateY(-2px);
    background-color: white;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 16px 20px;
  border: 2px solid ${props => props.hasError ? '#e53e3e' : '#e2e8f0'};
  border-radius: 12px;
  font-size: 1rem;
  font-family: inherit;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.9);
  resize: vertical;
  min-height: 120px;
  
  &::placeholder {
    color: #a0aec0;
    font-weight: 500;
  }
  
  &:focus {
    border-color: ${props => props.hasError ? '#e53e3e' : '#667eea'};
    outline: none;
    box-shadow: 0 0 0 3px ${props => props.hasError ? 'rgba(229, 62, 62, 0.1)' : 'rgba(102, 126, 234, 0.1)'};
    transform: translateY(-2px);
    background: white;
  }
`;

const SubmitError = styled.div`
  color: #e53e3e;
  text-align: center;
  margin-bottom: 1rem;
  padding: 12px;
  background: rgba(229, 62, 62, 0.1);
  border-radius: 8px;
  border-left: 4px solid #e53e3e;
  font-weight: 500;
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 18px 24px;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: ${props => props.isSubmitting ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  margin-top: 1rem;
  
  &:hover {
    transform: ${props => props.isSubmitting ? 'none' : 'translateY(-3px)'};
    box-shadow: ${props => props.isSubmitting ? 'none' : '0 15px 30px rgba(102, 126, 234, 0.4)'};
    animation: ${props => props.isSubmitting ? 'none' : css`${pulse} 2s infinite`};
  }
  
  &:disabled {
    opacity: 0.7;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    transition: left 0.5s;
  }
  
  &:hover::before {
    left: 100%;
  }
`;

const Spinner = styled.div`
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 2px solid transparent;
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 10px;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const SuccessContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  background: linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), 
              url('https://images.unsplash.com/photo-1556761175-b413da4baf72?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80') center/cover;
`;

const ConfettiContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
`;

const ConfettiPiece = styled.div`
  position: absolute;
  width: 10px;
  height: 10px;
  animation: ${confetti} 3s ease-in-out forwards;
  border-radius: 2px;
`;

const SuccessCard = styled.div`
  background: white;
  padding: 3rem 2rem;
  border-radius: 20px;
  text-align: center;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  max-width: 400px;
  width: 90%;
  position: relative;
  z-index: 2;
  animation: ${fadeIn} 0.8s ease-out;
`;

const SuccessIcon = styled.div`
  color: #48bb78;
  margin-bottom: 1.5rem;
  animation: ${fadeIn} 0.8s ease-out 0.3s both;
`;

const SuccessTitle = styled.h2`
  color: #2d3748;
  font-size: 2rem;
  margin-bottom: 1rem;
  font-weight: 700;
  animation: ${fadeIn} 0.8s ease-out 0.5s both;
`;

const SuccessMessage = styled.p`
  color: #4a5568;
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
  animation: ${fadeIn} 0.8s ease-out 0.7s both;
`;

const SuccessSubmessage = styled.p`
  color: #718096;
  font-size: 1rem;
  margin-bottom: 2rem;
  animation: ${fadeIn} 0.8s ease-out 0.9s both;
`;

const BackButton = styled.button`
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
  color: white;
  border: none;
  padding: 14px 28px;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1rem;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(72, 187, 120, 0.3);
  }
`;

export default AppointmentForm;